import { useState } from "react";
import { numerWypowiedzi } from "@shared/numeracja.js";
import { MAX_OKNO_POMIARU_SEK } from "@shared/prosodyCore.js";
import { useKonto } from "../../hooks/useKonto.js";
import { Avatar } from "../ui/Avatar.js";
import { Badge } from "../ui/Badge.js";
import { Tooltip } from "../ui/Tooltip.js";
import type { UtteranceDto } from "../../lib/api.js";
import {
  ETYKIETY_JAKOSCI,
  ETYKIETY_RODZAJU,
  WARIANTY_JAKOSCI,
  type JakoscWypowiedzi,
} from "../../lib/jakoscWypowiedzi.js";
import type { UwagaWypowiedzi } from "../../lib/raportOceny.js";
import { mmss } from "./czas.js";
import { poczatkiFragmentow, podzielNaFragmenty, type ZakresCytatu, type ZakresZaznaczenia } from "./fragmenty.js";
import { UwagiWypowiedzi } from "./UwagiWypowiedzi.js";
import { tekstZZaznaczeniem } from "./ZaznaczenieTrafien.js";

/** Wspolna pusta lista: dymek bez zaznaczen dostaje zawsze te sama referencje. */
const BRAK_ZAZNACZEN: readonly ZakresZaznaczenia[] = [];

interface Props {
  wypowiedz: UtteranceDto;
  /** Rotacyjny tint tozsamosci 1-4 (--avatar-1..4). */
  tint: number;
  zakresy: readonly ZakresCytatu[];
  /** Nazwa modelu, ktory wystawil ocene brzmienia. Do dymka przy adnotacji. */
  modelEkspresji: string | null;
  biezaca: boolean;
  /** `null`, gdy nagrania nie ma - znacznik czasu przestaje byc kontrolka. */
  naPrzewin: ((ms: number) => void) | null;
  /**
   * False, gdy czasy wypowiedzi nie istnieja (tekst bez znacznikow czasu: startMs to 0, a nie
   * prawdziwe 0:00). Wtedy nie pokazujemy znacznika czasu wcale - ani jako przycisk, ani jako napis.
   */
  pokazCzas?: boolean;
  /**
   * Jakosc wypowiedzi wynikajaca ze zweryfikowanych wskazan oceny (shared/koloryWypowiedzi.ts). Brak wartosci to
   * wypowiedz bez wskazan: zostaje bez koloru, bo brak wskazania nie jest ocena "srednio".
   */
  jakosc?: JakoscWypowiedzi;
  /** Chwilowe wyroznienie po przejsciu z karty oceny ("Pokaz w transkrypcie"). */
  wyrozniona?: boolean;
  /**
   * Trafienia powtarzanego slowa zaznaczone na czas wyroznienia (przejscie z listy "Powtarzane slowa"), juz sprawdzone
   * przez zaznaczeniaWTekscie. Brak: nic nie jest zaznaczone.
   */
  zaznaczenia?: readonly ZakresZaznaczenia[];
  /** Wskazania z oceny przy tej wypowiedzi (lib/raportOceny.ts, policzPodswietlenia). Brak: wypowiedz bez wskazan. */
  uwagi?: readonly UwagaWypowiedzi[];
  /** Przelacznik z legendy: false chowa wskazania i litery przy zaznaczonych cytatach, a same zaznaczenia zostaja. */
  pokazUwagi?: boolean;
}

/**
 * Jeden dymek transkryptu (CV-2 z odstepstwem: kazdy po lewej, patrz session.css).
 *
 * Adnotacja ma dwie polowy i one NIE sa rownowazne: `measured` to pomiar,
 * `interpreted` to opinia modelu. Wczesniej rozroznial je wylacznie krój
 * (kursywa), czyli styl byl jedynym nosnikiem znaczenia - wprost przeciw A-7
 * i ST-3. Teraz niesie je tekst: pomiar dostaje badge BD-1 wariant info
 * z sama wartoscia, ocena modelu badge BD-2 z jawnym prefiksem "ocena modelu".
 * Kolejnosc jest czescia komunikatu - najpierw fakty, potem opinia.
 */
export function Wypowiedz({
  wypowiedz: u,
  tint,
  zakresy,
  modelEkspresji,
  biezaca,
  naPrzewin,
  pokazCzas = true,
  jakosc,
  wyrozniona = false,
  zaznaczenia = BRAK_ZAZNACZEN,
  uwagi = [],
  pokazUwagi = true,
}: Props): React.JSX.Element {
  const [aktywnyZnacznik, setAktywnyZnacznik] = useState<string | null>(null);
  // Plakietki pomiaru i ocena brzmienia z nazwa modelu to dane techniczne: konto `user` widzi sam czas wypowiedzi.
  const { czyAdmin } = useKonto();
  const fragmenty = podzielNaFragmenty(u.text, zakresy);
  const poczatki = poczatkiFragmentow(fragmenty);
  const maUwagi = pokazUwagi && uwagi.length > 0;
  /** Litera uwagi po id wskazania, ktorego cytat jest zaznaczony: tak zaznaczenie wie, ktora jest jego uwaga. */
  const znacznikiPoId = new Map(
    uwagi.flatMap((uwaga) => (uwaga.znacznik === null ? [] : [[uwaga.id, uwaga.znacznik] as const])),
  );
  const model = u.emotions[0]?.model ?? modelEkspresji;
  const czas = mmss(u.startMs);
  const numer = numerWypowiedzi(u.index);
  // Stopka bez zawartosci nie zajmuje miejsca: jej min-height to 38 px pola trafienia czasu,
  // a tekst bez czasow i bez adnotacji nie ma w niej nic do pokazania.
  // TOO_LONG: wypowiedz jest dluzsza niz limit pomiaru, wiec nie ma przy niej zadnej etykiety tempa, tonu ani pauz.
  // Bez slowa o tym brak etykiet wygladalby jak "nie bylo czego mierzyc", a nie jak swiadome pominiecie.
  const pomiarPominiety = czyAdmin && u.qualityFlags.includes("TOO_LONG");
  const limitMinut = Math.round(MAX_OKNO_POMIARU_SEK / 60);
  const maStopke =
    pokazCzas ||
    pomiarPominiety ||
    (czyAdmin &&
      (u.annotation.measured.length > 0 ||
        Boolean(u.annotation.interpreted) ||
        Boolean(u.annotation.interpretedConflict)));

  return (
    <li
      className={`msg${maUwagi ? " msg--z-uwagami" : ""}${biezaca ? " is-biezaca" : ""}${wyrozniona ? " is-wyrozniona" : ""}`}
      data-tint={tint}
      data-index={u.index}
      data-jakosc={jakosc}
      aria-current={biezaca ? "true" : undefined}
      // Fokus programowy po przejsciu z karty oceny (Rozmowa); nie jest w kolejnosci Taba.
      tabIndex={-1}
    >
      <div className="msg__ident">
        {/* Jedyny numer w transkrypcie: numer wypowiedzi od 1 (shared/numeracja.ts), ten sam w karcie oceny,
            w poleceniu dla modelu i w pliku Word. Stoi w osobnej kolumnie po lewej, poza dymkiem. */}
        <span className="msg__numer">
          <span className="tylko-dla-czytnika">Wypowiedź </span>
          {numer}
        </span>
        <span className="msg__avatar">
          <Avatar nazwa={u.speaker} rozmiar="s" />
        </span>
        <span className="msg__nazwa">{u.speaker}</span>
        {/* A-7: kolor dymka nie niesie znaczenia sam, wiec jakosc ma tez slowo: zlota plakietka przy najlepszych, szara
            przy najslabszych, neutralna przy srednich (dymek sredni nie ma koloru, wiec tylko slowo odroznia go od
            wypowiedzi bez wskazan). Zlota i szara maja tez kropke (Badge chowa ja przy neutralnej): szary tint jest
            bardzo blisko neutralnego, wiec "do wymiany" od "bez oznaczenia" odroznia tez ksztalt, nie tylko slowo. */}
        {jakosc && (
          <Badge wariant={WARIANTY_JAKOSCI[jakosc]} kropka>
            {ETYKIETY_JAKOSCI[jakosc]}
          </Badge>
        )}
      </div>

      <div className="bubble">
        {fragmenty.map((f, i) => {
          if (f.rodzaj !== "cytat") return <span key={i}>{tekstZZaznaczeniem(f.tekst, poczatki[i], zaznaczenia)}</span>;
          const znacznik = maUwagi ? (znacznikiPoId.get(f.id) ?? null) : null;
          // Litera stoi ZA zaznaczonym cytatem, a nie w nim: zloty marker mocnej strony to tlo, wiec litera w srodku
          // poszerzylaby go poza cytowane slowa. Najechanie dziala na calej parze (cytat i litera), dlatego zdarzenia
          // niesie owijka bez wlasnego wygladu, a wyroznienie (data-aktywny) zostaje na samym cytacie.
          return (
            <span
              onMouseEnter={znacznik === null ? undefined : () => setAktywnyZnacznik(znacznik)}
              onMouseLeave={znacznik === null ? undefined : () => setAktywnyZnacznik(null)}
              key={i}
            >
              <span
                className="cytat-podswietlony"
                data-rodzaj={f.rodzajWskazania}
                data-aktywny={znacznik !== null && znacznik === aktywnyZnacznik ? "true" : undefined}
              >
                {tekstZZaznaczeniem(f.tekst, poczatki[i], zaznaczenia)}
                <span className="tylko-dla-czytnika">
                  {` (cytat ze wskazania${znacznik === null ? "" : ` ${znacznik}`}: ${ETYKIETY_RODZAJU[f.rodzajWskazania]})`}
                </span>
              </span>
              {znacznik !== null && (
                <sup className="cytat-znacznik" aria-hidden="true">
                  {znacznik}
                </sup>
              )}
            </span>
          );
        })}
        {biezaca && <span className="tylko-dla-czytnika"> - wypowiedź odtwarzana teraz</span>}
      </div>

      {maStopke && (
        <div className="msg__stopka">
          {czyAdmin &&
            u.annotation.measured.map((m) => (
              <Badge wariant="info" key={m}>
                {m}
              </Badge>
            ))}

          {pomiarPominiety && (
            <Tooltip
              tekst={`Ta wypowiedź trwa dłużej niż ${limitMinut} minut. Tempa, tonu ani pauz nie liczymy dla tak długiej jednej wypowiedzi: wartość uśredniona z tylu minut nie opisuje sposobu mówienia, więc nie podajemy liczby.`}
            >
              <Badge>{`bez pomiaru mowy: wypowiedź dłuższa niż ${limitMinut} min`}</Badge>
            </Tooltip>
          )}

          {czyAdmin && u.annotation.interpreted && (
            <Tooltip
              tekst={
                model
                  ? `Ocena brzmienia z modelu ${model}. To interpretacja, nie pomiar. Model nie dostaje transkrypcji, ale słowa słyszy w nagraniu - dlatego opis tempa i tonu zestawiamy z pomiarem.`
                  : "Interpretacja modelu, nie pomiar."
              }
            >
              <Badge>
                {`ocena modelu: ${u.annotation.interpreted}`}
                {model && <span className="tylko-dla-czytnika">{` - model ${model}`}</span>}
              </Badge>
            </Tooltip>
          )}

          {/* Opis modelu przeczyl pomiarowi, wiec nie stoi obok etykiet pomiaru jako
              rownorzedny fakt. Plakietka mowi o tym wprost, a pelna tresc modelu jest
              w dymku - nie ukrywamy jej, przestaje tylko uchodzic za wynik. Plakietka
              neutralna (BD-2), jak "ocena modelu": wariant info to w tym dymku pomiar,
              wiec wygladalaby jak wynik pomiaru, a warning odpada, bo w transkrypcie
              bursztyn (zloto) znaczy "najlepsze" w ocenie. */}
          {czyAdmin && u.annotation.interpretedConflict && (
            <Tooltip tekst={u.annotation.interpretedConflict}>
              <Badge>opis modelu odrzucony: przeczył pomiarowi</Badge>
            </Tooltip>
          )}

          {pokazCzas &&
            (naPrzewin ? (
              <button
                type="button"
                className="msg__czas"
                onClick={() => naPrzewin(u.startMs)}
                aria-label={`Przewiń nagranie do ${czas}`}
              >
                {czas}
              </button>
            ) : (
              <span className="msg__czas msg__czas--statyczny">{czas}</span>
            ))}
        </div>
      )}

      {maUwagi && (
        <UwagiWypowiedzi
          numer={numer}
          uwagi={uwagi}
          aktywnyZnacznik={aktywnyZnacznik}
          onAktywny={setAktywnyZnacznik}
        />
      )}
    </li>
  );
}
